import axios from 'axios';
import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getSubmissionBackendUrl } from '../../constants/api-urls';
import {
  isPlausibleSplitInviteToken,
  PENDING_SPLIT_INVITE_CLAIM_KEY,
  PENDING_SPLIT_INVITE_TOKEN_KEY,
} from '../../constants/splitInvite';
import { getCurrentUserProfileObjectId } from '../../utils';
import { getAuth0AccessToken } from '../../utils/auth0Session';

/** Auth bootstrap / public routes where invite navigation must not interrupt login. */
const AUTH_BOOTSTRAP_PATHS = new Set([
  '/shift',
  '/login',
  '/callback',
  '/created',
  '/invite',
]);

const isAuthBootstrapPath = (pathname: string): boolean => {
  if (AUTH_BOOTSTRAP_PATHS.has(pathname)) return true;
  if (pathname.startsWith('/invite/')) return true;
  return false;
};

const isAuthenticated = (): boolean => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    return Boolean(userInfo?.userId && (userInfo?.token || localStorage.getItem('access_token')));
  } catch {
    return false;
  }
};

/**
 * Single coordinator for split invite claim:
 * reserve after Auth0 login, finalize once Spotify curator profile exists.
 * No-op for users without a pending invite.
 */
const InviteClaimCoordinator: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inFlightRef = useRef(false);
  const lastFinalizeProfileRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    // Never navigate away while login/plan bootstrap is running on /shift etc.
    // Still allow reserve in the background once authenticated, but do not
    // force /campaigns until the user is past auth bootstrap.
    const canNavigateAfterClaim = () => !isAuthBootstrapPath(location.pathname);

    const clearRawToken = () => {
      try {
        sessionStorage.removeItem(PENDING_SPLIT_INVITE_TOKEN_KEY);
      } catch {
        // ignore
      }
    };

    const markPendingClaim = () => {
      try {
        sessionStorage.setItem(PENDING_SPLIT_INVITE_CLAIM_KEY, '1');
      } catch {
        // ignore
      }
    };

    const clearPendingClaim = () => {
      try {
        sessionStorage.removeItem(PENDING_SPLIT_INVITE_CLAIM_KEY);
      } catch {
        // ignore
      }
    };

    const hasPendingClaimFlag = () => {
      try {
        return sessionStorage.getItem(PENDING_SPLIT_INVITE_CLAIM_KEY) === '1';
      } catch {
        return false;
      }
    };

    const getRawToken = () => {
      try {
        return sessionStorage.getItem(PENDING_SPLIT_INVITE_TOKEN_KEY);
      } catch {
        return null;
      }
    };

    const resolveOwnProfileId = async (): Promise<string | null> => {
      try {
        const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
        // Never attach invites to a shared (team) profile — only the Auth0 owner's profile.
        if (spotifyUser?.isShared === true) {
          return null;
        }
      } catch {
        // continue with normal resolution
      }
      return getCurrentUserProfileObjectId();
    };

    const finalizeIfPossible = async (accessToken: string) => {
      const profileId = await resolveOwnProfileId();
      if (!profileId || cancelled) {
        return false;
      }

      // Avoid re-finalizing the same profile in a tight loop.
      if (lastFinalizeProfileRef.current === profileId) {
        return true;
      }

      const submissionBackendUrl = getSubmissionBackendUrl();
      const response = await axios.post(
        `${submissionBackendUrl}/curatorCampaign/invite-claims/finalize`,
        { curatorProfileId: profileId },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (cancelled) return true;

      if (response?.data?.success) {
        lastFinalizeProfileRef.current = profileId;
        clearPendingClaim();
        clearRawToken();
        toast.success('Campaign invitation added.');
        if (canNavigateAfterClaim()) {
          navigate('/campaigns', { replace: true });
        }
        return true;
      }

      return false;
    };

    const run = async () => {
      if (cancelled || inFlightRef.current) return;
      if (!isAuthenticated()) return;
      // On /shift the userInfo token may exist mid-hydration; avoid claim navigation races.
      if (isAuthBootstrapPath(location.pathname) && !getRawToken() && !hasPendingClaimFlag()) {
        return;
      }

      const rawToken = getRawToken();
      const pendingFlag = hasPendingClaimFlag();

      if (!isPlausibleSplitInviteToken(rawToken) && !pendingFlag) {
        return;
      }

      const accessToken = getAuth0AccessToken();
      if (!accessToken) return;

      inFlightRef.current = true;
      const submissionBackendUrl = getSubmissionBackendUrl();

      try {
        if (isPlausibleSplitInviteToken(rawToken)) {
          const reserveResponse = await axios.post(
            `${submissionBackendUrl}/curatorCampaign/invite-claims/reserve`,
            { token: rawToken },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (cancelled) return;

          if (reserveResponse?.data?.success) {
            clearRawToken();
            markPendingClaim();

            const claimState = reserveResponse?.data?.data?.claimState;
            if (claimState === 'redeemed') {
              clearPendingClaim();
              toast.success('Campaign invitation added.');
              if (canNavigateAfterClaim()) {
                navigate('/campaigns', { replace: true });
              }
              return;
            }
          } else {
            clearRawToken();
            clearPendingClaim();
            return;
          }
        }

        // Reserved (or flag from prior page): finalize when profile exists.
        if (hasPendingClaimFlag() || pendingFlag) {
          const done = await finalizeIfPossible(accessToken);
          if (!done && !cancelled) {
            // Profile not ready yet — keep pending flag; poll lightly.
            if (!pollTimer) {
              pollTimer = setInterval(() => {
                if (cancelled) return;
                finalizeIfPossible(accessToken).catch(() => {
                  // transient only; keep pending for next opportunity
                });
              }, 4000);
            }
          }
        }
      } catch (error: any) {
        if (cancelled) return;

        const status = error?.response?.status;
        const code = error?.response?.data?.code;

        // Auth0 access token expired/missing — keep invite for retry after re-login.
        if (status === 401 || code === 'auth_required' || code === 'auth_invalid' || code === 'auth_transient') {
          return;
        }

        // Invite terminal states: stop retrying this token/claim.
        if (status === 403 || status === 410) {
          clearRawToken();
          clearPendingClaim();
          if (status === 410) {
            toast.error('This invitation is no longer available.');
          }
          return;
        }

        if (status === 404 && code === 'no_pending_claim') {
          // Reserved claim not found server-side; drop flag but keep raw token if still present.
          clearPendingClaim();
          return;
        }

        // Transient network / 5xx: leave token/flag for retry.
      } finally {
        inFlightRef.current = false;
      }
    };

    run();

    // Re-run when storage may indicate Spotify connection completed.
    const onStorage = (event: StorageEvent) => {
      if (
        event.key === 'spotifyUser' ||
        event.key === 'userInfo' ||
        event.key === 'hasSpotifyConnection' ||
        event.key === 'access_token'
      ) {
        run();
      }
    };

    window.addEventListener('storage', onStorage);

    // Same-tab spotify connect updates localStorage without a storage event.
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        run();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    // Periodic light check while a claim is pending (Spotify profile creation).
    const softPoll = setInterval(() => {
      if (hasPendingClaimFlag() || isPlausibleSplitInviteToken(getRawToken())) {
        run();
      }
    }, 8000);

    return () => {
      cancelled = true;
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', visibilityHandler);
      if (pollTimer) clearInterval(pollTimer);
      clearInterval(softPoll);
    };
  }, [navigate, location.pathname]);

  return null;
};

export default InviteClaimCoordinator;
